import { type FormikConfig } from 'formik'
import { mixed, object, string } from 'yup'

import {
  doesGraphHaveTooManyNodes,
  generateRecursiveGraphNodePaths
} from 'components/scenarios/utils/graph'
import { muiTheme } from 'mui-theme'

import type { ScenarioFormValues } from 'components/scenarios/create/create-utils'
import type { ScenarioGraph } from 'types/api/scenarios/scenario'

export type OnSubmitHandler = FormikConfig<ScenarioFormValues>['onSubmit']

export enum ScenarioPassCriteria {
  ALL_PASS = 'ALL_PASS',
  AT_LEAST_ONE_FAILED = 'AT_LEAST_ONE_FAILED'
}

export const ConcurrentScenarioFormBaseStyles = {
  maxWidth: muiTheme.breakpoints.values['xl'],
  px: 4,
  py: 3
}

export const ConcurrentScenarioFormMqStyles = {
  [muiTheme.breakpoints.up('md')]: { px: 7 }
}

export function shouldIgnorePathnameFromNavPrompt(pathname: string) {
  return (
    pathname.startsWith('/scenarios/edit') ||
    pathname.startsWith('/scenarios/new')
  )
}

export function getConcurrentScenarioFormValidationSchema() {
  return object().shape({
    name: string().required('A Scenario Name is required'),
    description: string(),
    hypothesis: string(),
    pass_criteria: string().oneOf(
      Object.values(ScenarioPassCriteria),
      'Invalid pass criteria selected'
    ),
    graph: mixed<ScenarioGraph>()
      .test(
        'has nodes',
        'Add a node to the graph to save this scenario',
        (graph) => {
          if (!graph || !graph.nodes) return false

          const graphNodePathMap = generateRecursiveGraphNodePaths(
            graph?.nodes,
            ['nodes']
          )

          return Boolean(Object.keys(graphNodePathMap).length)
        }
      )
      .test(
        'has too many nodes',
        'Scenario has reached the maximum number of nodes allowed',
        (graph) => (graph ? !doesGraphHaveTooManyNodes(graph) : false)
      )
      .required()
  })
}
